import { getLocationByAddress } from '@/helpers/geocoder';
import { getListingImageId } from '@/helpers/imageRotation';
import api from '@/store/api';
import { marketingApi } from '@/store/api/services/marketing/marketing';
import type {
  CreateLocationRequest,
  Geolocation,
  LocationDetail,
  LocationImageView,
  LocationMarketing,
  LocationMarketingRequest,
  LocationSuggestion,
  LocationUploadImageRequest,
  LocationV2UpdateRequest,
  LocationV2UpdateResponse,
} from '@/types';
import type { ApiResponse } from '@bitstopco/bitstop-theme';
import { athena, atlas } from '../../instances';

export const locationApi = api.injectEndpoints({
  endpoints: (build) => ({
    getLocation: build.query<ApiResponse<LocationDetail>, number>({
      query: (id) => athena({ url: `v2/locations/${id}` }),
      providesTags: (_, __, location_id) => [{ type: 'Location', id: location_id }],
    }),
    // Unlike getLocation above, this isn't scoped to the caller's own operator on the backend —
    // it's gated by Athena's `locations:view_any_operator` permission instead. This is what lets
    // marketing admins load full field data for a location belonging to a *different* operator
    // when editing it from the marketing listing view; getLocation 404s for those.
    getLocationForMarketingListing: build.query<ApiResponse<LocationDetail>, number>({
      query: (id) => athena({ url: `v2/locations/${id}/marketing-listing` }),
      providesTags: (_, __, location_id) => [{ type: 'Location', id: location_id }],
    }),
    getLocationGeolocation: build.query<Geolocation, string>({
      queryFn: async (address) => {
        try {
          const data = await getLocationByAddress(address);
          return { data };
        } catch {
          return {
            error: {
              status: 500,
              data: { message: 'Failed to fetch geolocation' },
            },
          };
        }
      },
    }),
    createLocation: build.mutation<ApiResponse<Location>, CreateLocationRequest>({
      query: (data) =>
        atlas({
          url: '/v2/locations',
          method: 'POST',
          data,
          showError: true,
        }),
      invalidatesTags: [{ type: 'LocationsSummary' }, { type: 'Locations' }],
    }),
    updateLocation: build.mutation<ApiResponse<LocationV2UpdateResponse>, LocationV2UpdateRequest>({
      query: (data) =>
        atlas({
          url: 'v2/locations',
          method: 'PATCH',
          data,
          showError: true,
        }),
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        if (!arg?.location_id) return;
        try {
          const { data: updateResponse } = await queryFulfilled;
          // Manually update the cache for immediate UI update
          const patchResult = dispatch(
            locationApi.util.updateQueryData('getLocation', arg.location_id, (draft) => {
              if (updateResponse?.data && draft?.data) {
                const d = updateResponse.data;
                // Update fields from the response
                if (d.name !== undefined) draft.data.location_name = d.name;
                // Atlas's swagger example shows `hours` as [{ day, hours }] here, which doesn't
                // match LocationDetail.hours (string[]) and would crash EditLocationSchedule's
                // string parsing if patched as-is. Only trust it when it's actually string[];
                // otherwise the tag invalidation below refetches the correct shape from Athena.
                if (Array.isArray(d.hours) && d.hours.every((h) => typeof h === 'string')) {
                  draft.data.hours = d.hours;
                }
                if (d.updated_at !== undefined) draft.data.updated_at = d.updated_at;
                if (d.full_address !== undefined) draft.data.full_address = d.full_address;
                if (d.street_address !== undefined) draft.data.street_address = d.street_address;
                if (d.address2 !== undefined) draft.data.address2 = d.address2;
                if (d.city !== undefined) draft.data.city = d.city;
                if (d.state !== undefined) draft.data.state = d.state;
                if (d.country !== undefined) draft.data.country = d.country;
                if (d.zipcode !== undefined) draft.data.zipcode = d.zipcode;
                if (d.place_id !== undefined) draft.data.place_id = d.place_id;
              }
              // Patch request-only fields into cache (response may not include them)
              if (draft?.data && arg) {
                if (arg.vault_branch !== undefined) draft.data.vault_branch = arg.vault_branch;
                if (arg.service_branch !== undefined)
                  draft.data.service_branch = arg.service_branch;
                if (arg.profile_completed !== undefined)
                  draft.data.profile_completed = arg.profile_completed;
              }
            }),
          );
          // Invalidate tags for list and other queries. MarketingListing is invalidated too
          // since the marketing Listing Single View reads this same location via a separate
          // cache entry (useGetMarketingListingQuery) and must reflect edits made here.
          dispatch(
            locationApi.util.invalidateTags([
              { type: 'Location', id: arg.location_id },
              { type: 'Locations' },
              { type: 'MarketingListing', id: arg.location_id },
            ]),
          );
          // Revert on error
          queryFulfilled.catch(patchResult.undo);
        } catch {
          // Error handled in component
        }
      },
    }),
    deleteLocation: build.mutation<ApiResponse<any>, number>({
      query: (id) =>
        atlas({
          url: `v2/locations/${id}`,
          method: 'DELETE',
          showError: true,
        }),
      invalidatesTags: (_, __, location_id) => [
        { type: 'Location', location_id: location_id },
        { type: 'Locations' },
      ],
    }),
    uploadLocationImage: build.mutation<ApiResponse<LocationImageView>, LocationUploadImageRequest>(
      {
        query: ({ locationId, file }) => {
          const formData = new FormData();
          formData.append('file', file);

          return atlas({
            url: `v2/locations/${locationId}/images`,
            method: 'POST',
            data: formData,
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
        },
        async onQueryStarted({ locationId }, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            const image = data?.data;
            if (!image?.url) return;
            dispatch(
              locationApi.util.updateQueryData('getLocation', locationId, (draft) => {
                if (!draft?.data) return;
                draft.data.image_urls = [...(draft.data.image_urls ?? []), image.url];
                if (draft.data.images) draft.data.images.push({ id: image.id, url: image.url });
              }),
            );
            dispatch(
              marketingApi.util.updateQueryData('getMarketingListing', locationId, (draft) => {
                draft?.data?.image_urls?.push({ id: image.id, url: image.url });
              }),
            );
          } catch {}
        },
      },
    ),
    deleteLocationImage: build.mutation<ApiResponse<any>, { locationId: number; imageId: string }>({
      query: ({ locationId, imageId }) =>
        atlas({
          url: `/v2/locations/${locationId}/images/${imageId}`,
          method: 'DELETE',
        }),
      async onQueryStarted({ locationId, imageId }, { dispatch, queryFulfilled }) {
        const isOther = (url: string) => getListingImageId({ url }) !== imageId;
        try {
          await queryFulfilled;
          dispatch(
            locationApi.util.updateQueryData('getLocation', locationId, (draft) => {
              if (!draft?.data) return;
              draft.data.image_urls = draft.data.image_urls?.filter(isOther);
              if (draft.data.images) {
                draft.data.images = draft.data.images.filter(
                  (i) => getListingImageId(i) !== imageId,
                );
              }
            }),
          );
          dispatch(
            marketingApi.util.updateQueryData('getMarketingListing', locationId, (draft) => {
              if (!draft?.data) return;
              draft.data.image_urls = draft.data.image_urls.filter(
                (i) => getListingImageId(i) !== imageId,
              );
            }),
          );
        } catch {}
      },
    }),
    submitLocationForReview: build.mutation<
      ApiResponse<LocationMarketing>,
      LocationMarketingRequest
    >({
      query: ({ locationId, user }) => {
        return atlas({
          url: `/v2/locations/${locationId}/marketing`,
          method: 'POST',
          data: { action: 'ask_for_review', user },
          showError: true,
        });
      },
      invalidatesTags: (_, __, { locationId }) => [
        { type: 'LocationNotes', location_id: locationId },
        { type: 'Location', id: locationId },
        { type: 'Locations' },
        { type: 'MarketingListing', id: locationId },
      ],
    }),
    unpublishLocation: build.mutation<ApiResponse<LocationMarketing>, LocationMarketingRequest>({
      query: ({ locationId, user }) => {
        return atlas({
          url: `/v2/locations/${locationId}/marketing`,
          method: 'POST',
          data: { action: 'unpublish', user },
          showError: true,
        });
      },
      invalidatesTags: (_, __, { locationId }) => [
        { type: 'Locations' },
        { type: 'Location' },
        { type: 'LocationNotes', location_id: locationId },
      ],
    }),
    getLocationSuggestion: build.query({
      query: (locationId) =>
        athena({
          url: `v2/locations/${locationId}/suggestion`,
        }),
      providesTags: (_, __, location_id) => [{ type: 'LocationSuggestion', location_id }],
    }),
    approveLocationSuggestion: build.mutation<
      ApiResponse<LocationSuggestion>,
      { locationId: number; resolvedBy: string }
    >({
      query: ({ locationId, resolvedBy }) =>
        atlas({
          url: `v2/locations/${locationId}/suggestion/approve`,
          method: 'POST',
          data: { resolved_by: resolvedBy },
        }),
      invalidatesTags: (_, __, { locationId }) => [
        { type: 'LocationSuggestion', location_id: locationId },
        { type: 'Location', id: locationId },
        { type: 'LocationNotes', location_id: locationId },
        { type: 'LocationsSummary', location_id: locationId },
      ],
    }),
    declineLocationSuggestion: build.mutation<
      ApiResponse<LocationSuggestion>,
      { locationId: number; resolvedBy: string }
    >({
      query: ({ locationId, resolvedBy }) =>
        atlas({
          url: `v2/locations/${locationId}/suggestion/decline`,
          method: 'POST',
          data: { resolved_by: resolvedBy },
        }),
      invalidatesTags: (_, __, { locationId }) => {
        return [
          { type: 'Location', id: locationId },
          { type: 'LocationSuggestion', location_id: locationId },
          { type: 'LocationNotes', location_id: locationId },
          { type: 'LocationsSummary', location_id: locationId },
        ];
      },
    }),
  }),
});

export const {
  useGetLocationQuery,
  useLazyGetLocationQuery,
  useGetLocationForMarketingListingQuery,
  useGetLocationGeolocationQuery,
  useCreateLocationMutation,
  useUpdateLocationMutation,
  useDeleteLocationMutation,
  useUploadLocationImageMutation,
  useDeleteLocationImageMutation,
  useSubmitLocationForReviewMutation,
  useUnpublishLocationMutation,
  useGetLocationSuggestionQuery,
  useApproveLocationSuggestionMutation,
  useDeclineLocationSuggestionMutation,
} = locationApi;

export const {
  endpoints: {
    getLocation,
    getLocationGeolocation,
    createLocation,
    updateLocation,
    deleteLocation,
    uploadLocationImage,
    deleteLocationImage,
    submitLocationForReview,
    unpublishLocation,
    getLocationSuggestion,
  },
} = locationApi;
